import React, { useContext, useEffect, useState } from 'react';

import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import Popper from '@material-ui/core/Popper';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { AppContext } from '../../contexts/app-context';
import { goToRoutePage, redirectRoutePage } from '../../utils/helper';
import {
  AccountCircleIcon, DarkThemeOffIcon, DarkThemeOnIcon, MenuIcon, MoreIcon, SearchIcon
} from '../../utils/icons';
import {
  AppBar, Autocomplete, Box, Hidden, IconButton, InputAdornment, Menu, MenuItem, TextField,
  Toolbar
} from '../../utils/material-ui-core';
import AppLogo from '../app-logo';
import LoginProfile from '../login-user-profile';
import LeftDrawer from './left-drawer';

const defaultSearchItem: SearchItem = { groupBy: "", title: "", path: "" };

const NavigationBar: React.FC = (props: any) => {
  const pageStyles = LocalPageStyles();

  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const { writeLog, themeType, setThemeType, clientCompanies, programs, kits, appStatus } = useContext(AppContext);
  const [anchorPopperEl, setAnchorPopperEl] = React.useState<null | HTMLElement>(null);

  const [searchResults, setSearchResults] = React.useState<SearchItem[]>([defaultSearchItem]);
  const [searchResult, setSearchResult] = React.useState(defaultSearchItem);

  const openPopper = Boolean(anchorPopperEl);
  const popperId = openPopper ? "simple-popper" : undefined;
  const mobileMenuId = "primary-search-account-menu-mobile";

  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const toggleDarkTheme = () => {
    if (themeType === "dark") {
      setThemeType("light");
      localStorage.setItem("ThemeType", "light");
    } else {
      setThemeType("dark");
      localStorage.setItem("ThemeType", "dark");
    }
    redirectRoutePage("");
  };

  const closeDrawer = () => setIsOpen(false);

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === "keydown" && ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")) {
      return;
    }

    setIsOpen(open);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorPopperEl(anchorPopperEl ? null : event.currentTarget);
  };

  const renderMobileMenu = (
    <Menu anchorEl={mobileMoreAnchorEl} anchorOrigin={{ vertical: "top", horizontal: "right" }} id={mobileMenuId} keepMounted transformOrigin={{ vertical: "top", horizontal: "right" }} open={isMobileMenuOpen} onClose={handleMobileMenuClose}>
      <MenuItem>
        <IconButton aria-describedby={popperId} aria-label="account of current user" aria-controls="primary-search-account-menu" aria-haspopup="true" color="inherit" onClick={handleClick}>
          <AccountCircleIcon />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  const handleAutoCompleteSearchChange = (e: any, newValue: any) => {
    const newValues = newValue.path.split("/");
    writeLog("Main Search Lookup", "Selected an item to view", newValues[2], newValues[1]);
    goToRoutePage(newValue.path);
  };

  useEffect(() => {
    const initAsync = async () => {
      if (clientCompanies.length || programs.length || kits.length) {
        const tempSearchResult: SearchItem[] = [];
        tempSearchResult.push({ groupBy: "", title: "", path: "" });
        clientCompanies.map((clientCompany) =>
          tempSearchResult.push({
            groupBy: "-- CLIENT COMPANIES --",
            title: clientCompany.name + " - " + clientCompany.hsbPartnerId,
            path: "onboarding/clientcompanies/" + clientCompany.clientCompanyKey,
          })
        );
        programs.map((program) =>
          tempSearchResult.push({
            groupBy: "-- PROGRAMS --",
            title: program.programName + " - " + program.programCode,
            path: "onboarding/programs/" + program.programKey,
          })
        );
        kits.map((kit) =>
          tempSearchResult.push({
            groupBy: "-- KITS --",
            title: kit.name + " - " + kit.hsbKitNo,
            path: "onboarding/kits/" + kit.kitKey,
          })
        );
        setSearchResult(defaultSearchItem);
        setSearchResults(tempSearchResult);
        setLoading(false);
      }
    };
    initAsync();
  }, [clientCompanies, programs, kits]);

  useEffect(() => {
    if (appStatus.locked) {
      goToRoutePage("Login");
    }
  }, [appStatus]);

  return (
    <Container maxWidth="xl">
      <Box className={pageStyles.boxRoot}>
        <CssBaseline />
        <AppBar position="fixed" elevation={0} color="transparent" classes={{ root: pageStyles.appbarRoot }}>
          <Toolbar className={pageStyles.toolBarRoot}>
            {!appStatus.locked ? (
              <IconButton edge="start" className={pageStyles.menuButton} color="inherit" aria-label="menu" onClick={toggleDrawer(true)}>
                <MenuIcon />
              </IconButton>
            ) : (
              <AppLogo />
            )}

            {!appStatus.locked ? (
              <Hidden smDown>
                <AppLogo />
              </Hidden>
            ) : null}

            <Box className={pageStyles.boxRoot}></Box>

            {!loading && searchResults.length > 2 ? (
              <Box className={pageStyles.search}>
                <Autocomplete
                  id="nav-search-text"
                  options={searchResults.sort((a, b) => -(b.groupBy + b.title).localeCompare(a.groupBy + a.title))}
                  groupBy={(option) => option.groupBy}
                  getOptionLabel={(option) => option.title}
                  getOptionSelected={(option, value) => option.title === value.title}
                  onChange={(e, data) => {
                    //setSearchResult(data);
                    handleAutoCompleteSearchChange(e, data);
                  }}
                  value={searchResult}
                  noOptionsText="No data to match your search"
                  disableClearable
                  handleHomeEndKeys
                  selectOnFocus
                  clearOnBlur
                  size="small"
                  autoHighlight={true}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className={pageStyles.autoSearch}
                      label=""
                      margin="normal"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        type: "search",
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            ) : null}

            <div className={pageStyles.grow} />

            <Box className={pageStyles.sectionDesktop}>
              <IconButton edge="end" aria-describedby={popperId} aria-label="Theme Toggle" aria-controls="menu-theme-toggle" aria-haspopup="true" onClick={toggleDarkTheme} color="inherit">
                {themeType === "dark" ? <DarkThemeOnIcon /> : <DarkThemeOffIcon />}
              </IconButton>
              <IconButton edge="end" aria-describedby={popperId} aria-label="login user profile" aria-controls="menu-login-user-profile" aria-haspopup="true" onClick={handleClick} color="inherit">
                <AccountCircleIcon />
              </IconButton>
              <Popper id={popperId} open={openPopper} anchorEl={anchorPopperEl} placement="bottom-end">
                <LoginProfile />
              </Popper>
            </Box>

            <Box className={pageStyles.sectionMobile}>
              <IconButton aria-label="show more" aria-controls={mobileMenuId} aria-haspopup="true" onClick={handleMobileMenuOpen} color="inherit">
                <MoreIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        {renderMobileMenu}
      </Box>
      <LeftDrawer isOpen={isOpen} callbackDrawerClose={closeDrawer} callbackToggleDarkTheme={toggleDarkTheme} />
    </Container>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    grow: {
      flexGrow: 1,
    },
    boxRoot: {
      flexGrow: 1,
    },
    appbarRoot: {
      display: "flex",
      justifyContent: "center",
      flexDirection: "row",
    },
    toolBarRoot: {
      backgroundColor: theme.palette.type === "dark" ? theme.palette.background.default : "#ffffff",
      width: "100%",
      height: "80px",
      maxWidth: "1894px",
      paddingLeft: theme.spacing(2),
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    sectionDesktop: {
      display: "none",
      visibility: "hidden",
    },
    sectionMobile: {
      display: "none",
      visibility: "hidden",
    },
    autoSearch: {
      maxWidth: "500px",
      [theme.breakpoints.up("sm")]: {
        width: "calc(100vh - 20vh)",
      },
      [theme.breakpoints.up("md")]: {
        width: "calc(100vh - 50vh)",
      },
      [theme.breakpoints.up("lg")]: {
        width: "calc(100vh - 40vh)",
      },
    },
    search: {
      position: "relative",
      borderRadius: theme.shape.borderRadius,
      marginRight: theme.spacing(1),
      marginLeft: 0,
      width: "100vw",
      [theme.breakpoints.up("sm")]: {
        marginLeft: theme.spacing(3),
        width: "auto",
      },
    },
    searchIcon: {
      padding: theme.spacing(0, 2),
      height: "100%",
      position: "absolute",
      pointerEvents: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  })
);

export default NavigationBar;
